import { createFileRoute, redirect } from '@tanstack/react-router';
import { WorkspacePicker, PickerPending, PickerError } from '@features/eor/WorkspacePicker';
import { workspaceDiscoveryService } from '@services/workspaceDiscoveryService';
import { redirectToUrl } from '@lib/loginRedirect';

export const Route = createFileRoute('/eor/select-workspace')({
  validateSearch: (s: Record<string, unknown>): { token: string } => ({
    token: typeof s.token === 'string' ? s.token : '',
  }),
  loaderDeps: ({ search }) => ({ token: search.token }),
  beforeLoad: ({ search }) => {
    if (!search.token) throw redirect({ to: '/eor/find' });
  },
  // verify-challenge is a one-time, attempt-limited COMMAND — run it exactly once
  // per navigation here (not via useQuery, which would re-POST and re-consume the
  // token on reconnect/remount). Business failures come back HTTP 200 with a
  // messageCode (workspaces === null) → returned as data; only network/5xx throw
  // → errorComponent.
  //
  // "No workspace linked to this email" is BE's K_WORKSPACE_NO_MATCH outcome
  // (workspaces === null) — route it to the dedicated no-workspace screen rather
  // than the generic "link problem" branch. (Older BE returned an empty array;
  // the length check is kept as a defensive fallback.)
  loader: async ({ deps }) => {
    const result = await workspaceDiscoveryService.verifyChallenge(deps.token);
    if (result.messageCode === 'K_WORKSPACE_NO_MATCH' || result.workspaces?.length === 0) {
      throw redirect({ to: '/eor/no-workspace', search: { email: '' } });
    }
    // Exactly one workspace → skip the picker and go straight to its login.
    // Only when it's active with a loginUrl; a lone inactive workspace still
    // renders the picker so the user sees the "Inactive" state. The pending
    // component shows while the full-page redirect completes.
    const only = result.workspaces?.length === 1 ? result.workspaces[0] : undefined;
    if (only?.active && only.loginUrl) {
      redirectToUrl(only.loginUrl);
      return await new Promise<never>(() => {});
    }
    return result;
  },
  component: WorkspacePicker,
  pendingComponent: PickerPending,
  errorComponent: PickerError,
});
